<template>
  <div ref="eventFormContainer">
    <div class="page-header bordered">
      <h1>
        {{ action === 'edit' ? 'Update Event' : 'Create Event'}}
      </h1>
    </div>

    <div v-if="stripeAccount.status === 'pending'" class="text-center">
      <b-alert variant="warning" show>
        To create Events you need to connect your &nbsp;
        <router-link to="/users/stripe-account">
            <i class="icon fa fa-cc-stripe"></i> Stripe Account
        </router-link>
      </b-alert>
    </div>

    <div v-else>
      <form>
        <ShowErrors
          :errorMessages="errorMessages"
        />

        <h3 class="subheadline">Event Details</h3>
        <div class="form-group">
          <label for="title" class="required">Title</label>
          <input type="text" class="form-control form-control-lg" name="title"
            v-model="event.title" data-vv-as="Title" autofocus
            placeholder="Give a short title for event..."  v-validate="'required|max:250'" />
          <span v-show="errors.has('title')" class="input-error">
            {{ errors.first('title') }}
          </span>
        </div>

        <div class="form-group">
          <label for="description" class="required">Description</label>
          <div v-show="markupDescriptionPreview" v-html="markedText(event.description || '')" />
          <textarea class="form-control form-control-lg" name="description"
                    v-model.trim="event.description" data-vv-as="Description" rows="4"
                    placeholder="Please, provide a description for event..."
                    v-validate="'required|max:100000'" v-show="!markupDescriptionPreview" />
          <span v-show="errors.has('description')" class="input-error">
              {{ errors.first('description') }}
            </span>

          <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
                  @click="markupDescriptionPreview = !markupDescriptionPreview">
            <i :class="['icon fa', {'fa-eye': !markupDescriptionPreview}, {'fa-edit': markupDescriptionPreview}]"></i>
            {{markupDescriptionPreview ? 'Edit Mode' : 'Preview'}}
          </button>
        </div>

        <h3 class="subheadline">Location</h3>
        <div class="form-group">
          <label for="venue_name" class="required">Venues Name</label>
          <input type="text" class="form-control form-control-lg" name="venue_name"
            v-model="event.venue_name"
            placeholder="Enter the venue's name"  v-validate="'required|max:250'"
            data-vv-as="Venues" />
          <span v-show="errors.has('venue_name')" class="input-error">
            {{ errors.first('venue_name') }}
          </span>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="address_1" class="required">Address 1</label>
              <input type="text" class="form-control form-control-lg" name="address_1"
                data-vv-as="Address 1" v-validate="'required|max:250'"
                placeholder="Address" v-model="event.address_1">
              <span v-show="errors.has('address_1')" class="input-error">
                {{ errors.first('address_1') }}
              </span>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="form-group">
              <label for="address_2">Address 2</label>
              <input type="text" class="form-control form-control-lg" name="address_2"
                data-vv-as="Address 2" placeholder="Address 2" v-model="event.address_2"
                v-validate="'max:250'">
              <span v-show="errors.has('address_2')" class="input-error">
                {{ errors.first('address_2') }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="city" class="required">City</label>
              <input type="text" class="form-control form-control-lg" name="city"
                data-vv-as="City" placeholder="City" v-model="event.city"
                v-validate="'required|max:250'">
              <span v-show="errors.has('city')" class="input-error">
                {{ errors.first('city') }}
              </span>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group">
              <label for="state" class="required">State</label>
              <select name="state" class="form-control form-control-lg ui-select"
                      v-model="event.state" v-validate="{ required: event.state }"
                      data-vv-as="State">
                <option value=""> State </option>
                <option v-for="(state, index) in stateList" :key="index"
                        :value="state.value">
                  {{ state.name }}
                </option>
              </select>
              <span v-show="errors.has('state')" class="input-error">
                {{ errors.first('state') }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="zip_code" class="required">Zip Code</label>
              <input type="text" class="form-control form-control-lg" name="zip_code"
                data-vv-as="Zip Code" placeholder="Zip Code" v-model="event.zip_code"
                v-validate="'required|max:15'">
              <span v-show="errors.has('zip_code')" class="input-error">
                {{ errors.first('zip_code') }}
              </span>
            </div>
          </div>
        </div>

        <h3 class="subheadline">Event Dates</h3>
        <div v-for="(eventDate) in event.event_dates_attributes" :key="eventDate.id">
          <EventDateForm
            :eventDate="eventDate"
            :disabled="event.purchases_count > 0 || false"
          />
        </div>

        <br/>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="show_in_time_zone">Show event dates in specific time zone</label>
                <b-form-checkbox switch size="lg" v-model="event.show_in_time_zone"
                  name="show_in_time_zone" data-vv-as="Show in time zone">
                </b-form-checkbox>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="form-group" v-if="event.show_in_time_zone">
              <label for="time_zone" class="required">Time zone</label>
              <select name="time_zone" class="form-control form-control-lg ui-select"
                v-model="event.time_zone" v-validate="{ required: event.show_in_time_zone }"
                data-vv-as="Time zone">
                <option value=""> Select time zone... </option>
                <option v-for="(timeZone, index) in timeZoneList" :key="index"
                  :value="timeZone.value">
                  {{ timeZone.name }}
                </option>
              </select>
              <span v-show="errors.has('time_zone')" class="input-error">
                {{ errors.first('time_zone') }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="event_time">Event time</label>
          <select name="event_time" class="form-control form-control-lg ui-select col-lg-5"
            v-model="event.event_time_cd">
            <option value="" disabled> Select event time... </option>
            <option value="0"> Early event </option>
            <option value="1"> Late event </option>
          </select>
        </div>

        <h3 class="subheadline">
          Event Image <small> (Maximum upload image size: 10MB) </small>
        </h3>
        <div class="form-group">
          <div class="image-preview" v-if="imageData.length > 0 || (event.image_url && event.image_url.length > 0)">
            <img class="preview" :src="imageData || event.image_url">
            <br/>
            <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
              @click="removeImage">
              <i class="icon fa fa-trash"></i> Remove Image
            </button>
            &nbsp;
            <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
              @click="changeImage">
              <i class="icon fa fa-trash"></i> Change Image
            </button>
          </div>
          <div v-else class="ui-dropzone" @click="changeImage">
            <div class="icon"></div>
            <div>Click to upload an image</div>
          </div>
          <input type="file" class="form-control form-control-lg" ref="eventImage"
            @change="previewImage" accept="image/png, image/jpeg" style="display: none;"
            data-max-size="12228" data-vv-as="Image" name="image" id="event-image"
            v-validate="'image|ext:png,jpg|size:12228'">

          <span v-show="errors.has('image')" class="input-error">
            {{ errors.first('image') }}
          </span>
        </div>

        <h3 class="subheadline">Tickets</h3>
        <div v-for="(ticket, index) in event.tickets_attributes"
          :key="'ticket-' + index" v-show="showElement(ticket)">
          <TicketForm
            :absorbFees="ticket.ticket_cost_cd === 0"
            :ticket="ticket"
            :index="index"
            :applicationSettings="applicationSettings"
            :userSettings="userSettings"
            :showRemoveTicketButton="index !== 0"
            @remove-ticket="removeElement"
            @update-remaining-tickets="updateRemainingTickets"
          />
        </div>

        <b-button class="btn btn-default btn-inverse float-right" size="sm"
          @click="addElement('ticket')">
          Add Ticket
        </b-button>
        <br/>

        <h3 class="subheadline">Additional Settings</h3>
        <div class="form-group">
          <label for="purchase_message" class="required">Next Steps After Purchase</label>
          <div v-show="markupPreview" v-html="markedText(this.event.purchase_message)" />
          <textarea class="form-control form-control-lg" name="purchase_message"
            v-model="event.purchase_message" v-validate="'required|max:5000'"
            rows="8" data-vv-as="Next Steps After Purchase" v-show="!markupPreview"/>
          <span v-show="errors.has('purchase_message')" class="input-error">
            {{ errors.first('purchase_message') }}
          </span>

          <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
            @click="markupPreview = !markupPreview">
            <i :class="['icon fa', {'fa-eye': !markupPreview}, {'fa-edit': markupPreview}]"></i>
            {{markupPreview ? 'Edit Mode' : 'Preview'}}
          </button>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="age_restriction" class="required">Age restriction</label>
              <select name="age_restriction" class="form-control form-control-lg ui-select"
                v-validate="'required'" data-vv-as="Age Restriction" v-model="event.age_restriction">
                <option value="18"> 18+ </option>
                <option value="21"> 21+ </option>
                <option value="0"> All ages </option>
              </select>
              <span v-show="errors.has('age_restriction')" class="input-error">
                {{ errors.first('age_restriction') }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="policy_refund" class="required">Refund Policy</label>
          <textarea class="form-control form-control-lg" name="policy_refund"
            v-model="event.policy_refund" data-vv-as="Refund Policy"
            placeholder="Please, provide refund policy for event..."
            v-validate="'required|max:3500'" rows="4"
            :disabled="(event.purchases_count > 0) ? true : false"/>
          <span v-show="errors.has('policy_refund')" class="input-error">
            {{ errors.first('policy_refund') }}
          </span>
        </div>

        <div class="form-group">
          <div class="checkbox">
            <input type="checkbox" v-model="event.send_purchase_email_to_admin"
              name="send_purchase_email_to_admin" id="send_purchase_email_to_admin"
              data-vv-as="Receive purchase email">
            <label for="send_purchase_email_to_admin">
              Receive an email for every purchase
            </label>
          </div>
        </div>

        <div class="form-group">
          <div class="checkbox">
            <input type="checkbox" v-model="event.accept_giftcards"
              name="accept_giftcards" id="accept_giftcards"
              data-vv-as="Remaining Tickets">
            <label for="accept_giftcards">
              Enable purchase with Gift Cards
            </label>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <div class="checkbox">
                <input type="checkbox" v-model="event.show_remaining_tickets"
                  name="show_remaining_tickets" id="show_remaining_tickets"
                  data-vv-as="Remaining Tickets">
                <label for="show_remaining_tickets">
                  Show the number of remaining tickets on your event listing
                </label>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="form-group" v-show="event.show_remaining_tickets">
              <div class="row">
                <div class="col-lg-8">
                  <label for="show_remaining_tickets_since">
                    when remaining tickets fall to this or fewer:
                  </label>
                  <span v-show="errors.has('show_remaining_tickets_since')" class="input-error">
                    {{ errors.first('show_remaining_tickets_since') }}
                  </span>
                </div>

                <div class="col-lg-4">
                  <input type="text" class="form-control form-control-lg" name="show_remaining_tickets_since"
                     v-model="event.show_remaining_tickets_since"
                     v-validate="'numeric'" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <br/>
        <hr/>

        <div class="form-group">
          <button type="button" class="btn btn-lg btn-primary" @click="validate()">
            {{ action === 'edit' ? 'Update Event' : 'Create Event'}}
          </button>
          &nbsp;
          <button type="button" class="btn btn-lg btn-default" @click="goBack()">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import EventService from '../../../services/EventService'
import OrganizerProfileService from '../../../services/OrganizerProfileService'
import ApplicationSettingService from '../../../services/ApplicationSettingService'
import moment from 'moment-timezone'
import ShowErrors from '../../common/ShowErrors'
import TicketForm from '../../common/TicketForm'
import EventDateForm from '../../common/EventDateForm'
import axios from 'axios'

const objectToFormData = require('object-to-formdata')

const defaultValuePurchaseMessage = `**What to expect**

- Check in with the box office during 45 minutes before start the show with your name. No need to print out your tickets.
- Seating is first-come, first-served and begins 10 minutes before show starts.
- Last seating is 20 minutes after show starts. If you won't be arriving until after that, we will not be able to seat you.

**Need anything else?**

Let us know if you need help with any of the following:

- You are celebrating a special occasion with us.
- You need wheelchair access and seating.
- Anything is wrong with your order.
`

export default {
  name: 'EventForm',
  components: {
    ShowErrors,
    TicketForm,
    EventDateForm
  },
  data () {
    return {
      action: this.$route.params.action,
      eventId: this.$route.params.id,
      eventToDuplicate: this.$route.params.event_to_duplicate,
      timeZoneList: this.$timeZoneList,
      stateList: this.$stateList,
      markupPreview: false,
      markupDescriptionPreview: false,
      stripeAccount: {
        status: null
      },
      applicationSettings: {
        TICKET_FEE: {
          value: 0
        },
        TICKET_FEE_BASE_COST: {
          value: 0
        },
        TICKET_FEE_PERCENTAGE: {
          value: 0
        }
      },
      userSettings: {},
      moment: moment,
      errorMessages: [],
      imageData: '',
      organizerProfile: {},
      event: {
        id: null,
        title: null,
        description: null,
        image: null,
        policy_refund: 'Attendees can receive refunds to their original payment method or as a credit to a future event up to 24 hours before the event start date.',
        age_restriction: '18',
        event_time_cd: null,
        purchase_message: defaultValuePurchaseMessage,
        accept_giftcards: true,
        show_remaining_tickets: true,
        show_remaining_tickets_since: 0,
        show_in_time_zone: false,
        remove_image: null,
        send_purchase_email_to_admin: true,
        time_zone: null,
        venue_name: null,
        address_1: null,
        address_2: null,
        city: null,
        state: null,
        zip_code: null,
        event_dates_attributes: [
          {
            starts_at: null,
            ends_at: null,
            sales_starts_at: null,
            sales_ends_at: null
          }
        ],
        tickets_attributes: [
          {
            id: null,
            quantity: null,
            price: null,
            description: null,
            ticket_cost_cd: 1,
            purchase_limit: 1
          }
        ]
      }
    }
  },
  watch: {
    '$route' (to, from) {
      this.action = this.$route.params.action
      this.clearForm()
      this.removeImage()
      this.errors.clear()
    },
    'event.show_remaining_tickets' (newValue, oldValue) {
      if (!newValue) {
        this.event['show_remaining_tickets_since'] = 0
      }
    }
  },
  created () {
    this.getApplicationSettings()
    this.getUserSettings()
    this.getStripeAccountInfo()
    this.getEvent()
  },
  methods: {
    getEvent () {
      var self = this

      if (this.action === 'edit') {
        var loader = this.$loading.show()

        const eventRequest = EventService.getEvent(this.eventId)
        const organizerProfileRequest = OrganizerProfileService.getOrganizerProfile()

        axios.all([eventRequest, organizerProfileRequest])
          .then(axios.spread((eventResponse, organizerProfileResponse) => {
            self.event = eventResponse.data
            self.organizerProfile = organizerProfileResponse.data
          })).finally(() => {
            loader.hide()
          })
      } else {
        this.clearForm()

        // Set event to duplicate if exist
        if (this.eventToDuplicate) {
          this.duplicateEvent()
        } else {
          OrganizerProfileService.getOrganizerProfile().then((response) => {
            self.organizerProfile = response.data

            if (self.organizerProfile.enable_default_event_location) {
              self.event['venue_name'] = self.organizerProfile.venue_name
              self.event['address_1'] = self.organizerProfile.address_1
              self.event['address_2'] = self.organizerProfile.address_2
              self.event['city'] = self.organizerProfile.city
              self.event['state'] = self.organizerProfile.state
              self.event['zip_code'] = self.organizerProfile.zip_code
            }

            self.event['show_in_time_zone'] = (self.organizerProfile.time_zone || '').length > 0
            self.event['time_zone'] = self.organizerProfile.time_zone
          })
        }
      }
    },
    getStripeAccountInfo () {
      var self = this
      var stripeAccountReference = this.$localStorage.get('user_stripe_acocunt_reference')

      this.$http.get('/stripe_accounts/' + stripeAccountReference).then((response) => {
        self.stripeAccount = response.data
      })
    },
    getApplicationSettings () {
      var self = this

      ApplicationSettingService.getApplicationSetting().then((response) => {
        response.data.forEach((appSetting, index) => {
          self.applicationSettings[appSetting.name] = {
            value: appSetting.value,
            id: appSetting.id
          }
        })
      })
    },
    getUserSettings () {
      let self = this

      this.$http.get(`/user_settings`).then((response) => {
        const userEmail = self.$localStorage.get('user_email')

        response.data.filter((node) => node.user_email === userEmail).forEach((userSetting, _index) => {
          self.userSettings[userSetting.name] = {
            value: userSetting.value,
            id: userSetting.id
          }
        })
      })
    },
    showElement (element) {
      return !element.hasOwnProperty('_destroy')
    },
    removeImage () {
      this.imageData = ''
      this.event['image_url'] = ''
      this.event['image'] = null
      this.event['remove_image'] = true
    },
    changeImage () {
      this.$refs.eventImage.click()
    },
    previewImage: function (event) {
      // Reference to the DOM input element
      var input = event.target
      // Ensure that you have a file before attempting to read it
      if (input.files && input.files[0]) {
        var reader = new FileReader()
        reader.onload = (e) => {
          this.imageData = e.target.result
        }
        reader.readAsDataURL(input.files[0])

        this.event['image'] = input.files[0]
        this.event['remove_image'] = false
      }
    },
    addElement (type) {
      if (type === 'ticket') {
        this.event.tickets_attributes.push({
          quantity: null,
          price: 0,
          ticket_cost_cd: 1,
          description: 'General admission',
          purchase_limit: 1
        })
      } else if (type === 'eventDate') {
        this.event.event_dates_attributes.push({
          starts_at: null,
          ends_at: null,
          sales_starts_at: null,
          sales_ends_at: null
        })
      }
    },
    removeElement (index, element, type) {
      if (element['id']) {
        element['_destroy'] = 1

        if (type === 'ticket') {
          this.$set(this.event.tickets_attributes, index, element)
        }
      } else {
        if (type === 'ticket') {
          this.event.tickets_attributes.splice(index, 1)
        }
      }
    },
    updateRemainingTickets () {
      const reducer = (accum, ticket) => Number(ticket.quantity) + accum
      const total = this.event.tickets_attributes.reduce(reducer, 0)
      this.event['show_remaining_tickets_since'] = Math.round(total * 0.2)
    },
    validate () {
      var self = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          self.save()
        } else {
          self.$swal({
            title: 'Event form invalid!',
            text: 'Please check form errors',
            type: 'warning'
          })
        }
      })
    },
    save () {
      this.errorMessages = []
      var self = this
      var apiRequest = null
      var loader = this.$loading.show({
        container: this.$refs.eventFormContainer
      })

      // Remove image key if empty to build FormData object
      if (!this.event['image']) {
        delete this.event['image']
      }

      const formData = objectToFormData(this.event, null, null, 'event')

      if (this.action === 'edit') {
        apiRequest = EventService.updateEvent(this.event.id, formData)
      } else {
        apiRequest = EventService.createEvent(formData)
      }

      apiRequest.then(response => {
        self.$router.push('/users/events')
      }).catch(error => {
        if (error) {
          self.$swal({
            title: 'Event form invalid!',
            text: 'Please check form errors',
            type: 'warning'
          })

          console.log(error)
          try {
            var errorMsg = error.response.data.message || error.response.data.error
            self.errorMessages = (errorMsg && errorMsg.length > 0) ? [errorMsg] : error.response.data.errors[0]
          } catch (e) {
            console.log(e)
          }

          console.log(self.errorMessages)
        }
      }).finally(() => {
        loader.hide()
      })
    },
    clearForm () {
      this.event['id'] = ''
      this.event['title'] = ''
      this.event['description'] = ''
      this.event['image'] = null
      this.event['starts_at'] = ''
      this.event['ends_at'] = ''
      this.event['sales_starts_at'] = ''
      this.event['sales_ends_at'] = ''
      this.event['policy_refund'] = 'Attendees can receive refunds to their original payment method or as a credit to a future event up to 24 hours before the event start date.'
      this.event['age_restriction'] = '18'
      this.event['purchase_message'] = defaultValuePurchaseMessage
      this.event['event_time_cd'] = null
      this.event['accept_giftcards'] = true
      this.event['show_remaining_tickets'] = true
      this.event['show_remaining_tickets_since'] = 0
      this.event['show_in_time_zone'] = false
      this.event['time_zone'] = ''
      this.event['venue_name'] = ''
      this.event['address_1'] = ''
      this.event['address_2'] = ''
      this.event['city'] = ''
      this.event['state'] = ''
      this.event['zip_code'] = ''
      this.event['tickets_attributes'] = [
        {
          quantity: null,
          price: 0,
          ticket_cost_cd: 1,
          description: 'General admission',
          purchase_limit: 1
        }
      ]
      this.event['event_dates_attributes'] = [
        {
          starts_at: null,
          ends_at: null,
          sales_starts_at: null,
          sales_ends_at: null
        }
      ]
    },
    duplicateEvent () {
      var loader = this.$loading.show()
      var self = this

      EventService.getEvent(this.eventToDuplicate.id).then(response => {
        self.event = response.data
        self.event['duplicate_id'] = self.eventToDuplicate.id
        self.clearIdReferences()
      }).finally(() => {
        loader.hide()
      })
    },
    clearIdReferences () {
      this.event['id'] = null
      this.event['user_id'] = null
      this.event['event_dates_attributes'].forEach((eventDate) => {
        eventDate['id'] = null
        eventDate['event_id'] = null
        eventDate['starts_at'] = eventDate['starts_at_without_zone']
        eventDate['ends_at'] = eventDate['ends_at_without_zone']
        eventDate['sales_starts_at'] = eventDate['sales_starts_at_without_zone']
        eventDate['sales_ends_at'] = eventDate['sales_ends_at_without_zone']
      })
      this.event['tickets_attributes'].forEach((ticket) => {
        ticket['id'] = null
        ticket['event_id'] = null
      })
    },
    goBack () {
      this.$router.push('/users/events')
    }
  }
}
</script>
